import { Button } from '@chakra-ui/react';

import { CellContext, ColumnDef, createColumnHelper, HeaderContext } from '@tanstack/react-table';

import {
  DEFAULT_DATE_FORMAT,
  Order,
  CheckCircleOutline,
  CloseCircleOutline,
  EyeOutline,
} from '@aslanpay/portal-common-lib';
import { OrdersActions } from 'app/components/UI/Table/Table.types';
import { formatCurrency } from 'app/utils/strings';
import { format, parseISO } from 'date-fns';
import formatNumber from 'app/utils/formatting';

export type OrdersTableVariant = 'requested' | 'completed' | 'archived';

enum RequestedOrderField {
  createdAt = 'Order date',
  companyName = 'Company',
  pointsAmount = 'No. of points',
  moneyAmount = 'Cost',
}

enum CompletedOrderField {
  createdAt = 'Order date',
  companyName = 'Company',
  pointsAmount = 'No. of points',
  moneyAmount = 'Cost',
  completedAt = 'Paid on',
}

enum ArchivedOrderField {
  createdAt = 'Order date',
  companyName = 'Company',
  pointsAmount = 'No. of points',
  moneyAmount = 'Cost',
  archivedAt = 'Archived on',
}

function getRequestedOrdersColumns(columnHelper: ReturnType<typeof createColumnHelper<Order>>) {
  const fields = Object.keys(RequestedOrderField) as Array<keyof Order>;
  return fields.map((field) => {
    return columnHelper.accessor(field as keyof Order, {
      cell: (info) => {
        switch (field as keyof typeof RequestedOrderField) {
          case 'createdAt':
            return format(parseISO(info.getValue() as string), DEFAULT_DATE_FORMAT);
          case 'companyName':
            return info.getValue();
          case 'pointsAmount':
            return formatNumber(info.getValue() as number);
          case 'moneyAmount':
            return `${formatCurrency(info.getValue() as number, 'GBP')}`;
          default:
            return info.getValue();
        }
      },
      header: RequestedOrderField[field as keyof typeof RequestedOrderField],
      enableSorting: field === 'createdAt',
      meta: {
        getHeaderContext: (context: HeaderContext<Order, unknown>) => {
          if (['pointsAmount', 'moneyAmount'].includes(context.header.id)) {
            return { style: { justifyContent: 'end' } };
          }
          return undefined;
        },
        getCellContext: (context: CellContext<Order, unknown>) => {
          if (['pointsAmount', 'moneyAmount'].includes(context.column.id)) {
            return { style: { textAlign: 'right' } };
          }
          return undefined;
        },
      },
    });
  });
}

function getCompletedOrdersColumns(columnHelper: ReturnType<typeof createColumnHelper<Order>>) {
  const fields = Object.keys(CompletedOrderField) as Array<keyof Order>;

  return fields.map((field) => {
    return columnHelper.accessor(field as keyof Order, {
      cell: (info) => {
        switch (field as keyof typeof CompletedOrderField) {
          case 'createdAt':
            return format(parseISO(info.getValue() as string), DEFAULT_DATE_FORMAT);
          case 'companyName':
            return info.getValue();
          case 'pointsAmount':
            return formatNumber(info.getValue() as number);
          case 'moneyAmount':
            return `${formatCurrency(info.getValue() as number, 'GBP')}`;
          case 'completedAt':
            return format(parseISO(info.getValue() as string), DEFAULT_DATE_FORMAT);
          default:
            return info.getValue();
        }
      },
      header: CompletedOrderField[field as keyof typeof CompletedOrderField],
      enableSorting: field === 'createdAt' || field === 'completedAt',
      meta: {
        getHeaderContext: (context: HeaderContext<Order, unknown>) => {
          if (['pointsAmount', 'moneyAmount'].includes(context.header.id)) {
            return { style: { justifyContent: 'end' } };
          }
          return undefined;
        },
        getCellContext: (context: CellContext<Order, unknown>) => {
          if (['pointsAmount', 'moneyAmount'].includes(context.column.id)) {
            return { style: { textAlign: 'right' } };
          }
          return undefined;
        },
      },
    });
  });
}

function getArchivedOrdersColumns(columnHelper: ReturnType<typeof createColumnHelper<Order>>) {
  const fields = Object.keys(ArchivedOrderField) as Array<keyof Order>;

  return fields.map((field) => {
    return columnHelper.accessor(field as keyof Order, {
      cell: (info) => {
        switch (field as keyof typeof ArchivedOrderField) {
          case 'createdAt':
            return format(parseISO(info.getValue() as string), DEFAULT_DATE_FORMAT);
          case 'companyName':
            return info.getValue();
          case 'pointsAmount':
            return formatNumber(info.getValue() as number);
          case 'moneyAmount':
            return `${formatCurrency(info.getValue() as number, 'GBP')}`;
          case 'archivedAt':
            return format(parseISO(info.getValue() as string), DEFAULT_DATE_FORMAT);
          default:
            return info.getValue();
        }
      },
      header: ArchivedOrderField[field as keyof typeof ArchivedOrderField],
      enableSorting: field === 'createdAt' || field === 'archivedAt',
      meta: {
        getHeaderContext: (context: HeaderContext<Order, unknown>) => {
          if (['pointsAmount', 'moneyAmount'].includes(context.header.id)) {
            return { style: { justifyContent: 'end' } };
          }
          return undefined;
        },
        getCellContext: (context: CellContext<Order, unknown>) => {
          if (['pointsAmount', 'moneyAmount'].includes(context.column.id)) {
            return { style: { textAlign: 'right' } };
          }
          return undefined;
        },
      },
    });
  });
}

function getOrdersColumns(
  variant: OrdersTableVariant,
  ordersActions?: OrdersActions,
  isLoadingOrderDetails?: boolean,
  loadingOrderId?: string
) {
  const columnHelper = createColumnHelper<Order>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let columns: ColumnDef<Order, any>[];
  if (variant === 'requested') {
    // Base data columns first
    columns = getRequestedOrdersColumns(columnHelper);
  } else if (variant === 'completed') {
    columns = getCompletedOrdersColumns(columnHelper);
  } else {
    columns = getArchivedOrdersColumns(columnHelper);
  }

  // Spacer column to consume remaining horizontal space and push columns to the left and action buttons to the right
  const spacerWidth = variant === 'requested' ? '20%' : '0%';
  columns.push(
    columnHelper.display({
      id: 'spacer',
      meta: {
        getCellContext: () => ({
          style: { width: spacerWidth, padding: 0 },
        }),
        getHeaderContext: () => ({
          style: { width: spacerWidth, padding: 0 },
        }),
      },
      cell: () => null,
      header: () => null,
    })
  );

  if (variant === 'requested') {
    columns.push(
      columnHelper.display({
        id: 'view',
        meta: {
          getCellContext: () => ({
            style: { textAlign: 'center', width: '100px' },
          }),
          getHeaderContext: () => ({
            style: { textAlign: 'center', width: '100px' },
          }),
        },
        cell: ({ row }) => {
          const isThisOrderLoading = isLoadingOrderDetails && loadingOrderId === row.original.id;
          return (
            <Button
              variant="link"
              size="sm"
              color="brand.darkPurple"
              leftIcon={<EyeOutline boxSize="20px" />}
              onClick={() => ordersActions?.view(row.original as Order)}
              isLoading={isThisOrderLoading}
            >
              View
            </Button>
          );
        },
      })
    );

    columns.push(
      columnHelper.display({
        id: 'archive',
        meta: {
          getCellContext: () => ({
            style: { textAlign: 'center', width: '120px' },
          }),
          getHeaderContext: () => ({
            style: { textAlign: 'center', width: '120px' },
          }),
        },
        cell: ({ row }) => (
          <Button
            variant="link"
            size="sm"
            color="red"
            leftIcon={<CloseCircleOutline boxSize="20px" />}
            onClick={() => ordersActions?.archive(row.original as Order)}
          >
            Archive
          </Button>
        ),
      })
    );

    columns.push(
      columnHelper.display({
        id: 'complete',
        meta: {
          getCellContext: () => ({
            style: { textAlign: 'center', width: '120px' },
          }),
          getHeaderContext: () => ({
            style: { textAlign: 'center', width: '120px' },
          }),
        },
        cell: ({ row }) => (
          <Button
            variant="link"
            size="sm"
            color="brand.purple"
            leftIcon={<CheckCircleOutline boxSize="20px" />}
            onClick={() => ordersActions?.complete(row.original as Order)}
          >
            Mark as paid
          </Button>
        ),
      })
    );
  }

  return columns;
}

export default getOrdersColumns;
